import * as React from "react";
import { IOnboardingFolder } from "../../../../models/onboarding-tooltip";
import OnboardingTooltipService from "../../../../services/onboarding-tooltip-service";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaModalIconWrapper, {
  type KuikaModalIconIntent
} from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./onboarding-folder-modal.scss";

export interface IOnboardingFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folder?: IOnboardingFolder;
  parentFolderId?: string;
}

export interface IOnboardingFolderModalState {
  name: string;
  parentId?: string;
  folders: IOnboardingFolder[];
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
}

/** What each commit is doing, rather than the generic "Saving". */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };
const UPDATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Updating", success: "Updated" };

export class OnboardingFolderModal extends React.PureComponent<
  IOnboardingFolderModalProps,
  IOnboardingFolderModalState
> {
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IOnboardingFolderModalProps) {
    super(props);
    const folder = props.folder;
    this.state = {
      name: folder?.name || "",
      parentId: folder?.parentId || props.parentFolderId,
      folders: [],
      saveStatus: "idle"
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  async componentDidMount() {
    const folders = await OnboardingTooltipService.getFolders();
    this.setState({ folders });
  }

  handleNameChange = (_: any, value: string | number) => {
    this.setState({ name: String(value) });
  };

  handleParentChange = (value: string | number | Array<string | number> | null) => {
    const parentId = value === "root" || value === null ? undefined : String(value);
    this.setState({ parentId });
  };

  handleSave = async () => {
    const { folder, onClose } = this.props;
    const { name, parentId } = this.state;
    // A second press while the folder is saved or its ✓ is held would save it twice.
    if (!this.saveStatus.canStart) return;

    const folderData: Partial<IOnboardingFolder> = {
      id: folder?.id,
      name,
      parentId
    };

    this.saveStatus.start();
    try {
      await OnboardingTooltipService.saveFolder(folderData);
      // The ✓ goes up first; the dialog leaves after it and the panel reloads its folder tree.
      this.saveStatus.succeed(onClose);
    } catch {
      // Before, a refused save was swallowed with the dialog left as it was; now the button says so.
      this.saveStatus.fail();
    }
  };

  /** Cancel, the X and Escape. During the ✓ hold the dialog is already on its way out. */
  handleClose = (): void => {
    if (this.saveStatus.current === "success") return;
    this.props.onClose();
  };

  getAvailableParentFolders = (): Array<{ value: string; label: string }> => {
    const { folder } = this.props;
    const { folders } = this.state;

    const getChildIds = (id: string): string[] => {
      const children = folders.filter((f) => f.parentId === id);
      let ids = children.map((c) => c.id);
      children.forEach((child) => {
        ids = [...ids, ...getChildIds(child.id)];
      });
      return ids;
    };

    const excludeIds = folder ? [folder.id, ...getChildIds(folder.id)] : [];

    const availableFolders = folders
      .filter((f) => !excludeIds.includes(f.id))
      .map((f) => ({
        value: f.id,
        label: f.name
      }));

    return [{ value: "root", label: "Root (No Parent)" }, ...availableFolders];
  };

  render() {
    const { isOpen, folder } = this.props;
    const { name, parentId, saveStatus } = this.state;

    const isEdit = !!folder;
    const header: string = isEdit ? "Edit Folder" : "Create Folder";
    const intent: KuikaModalIconIntent = isEdit ? "edit" : "create";

    const footer = (
      <KModalFooter
        onCancel={this.handleClose}
        submitLabel={isEdit ? "Update" : "Create"}
        onSubmit={this.handleSave}
        disabled={!name.trim()}
        status={saveStatus}
        statusLabels={isEdit ? UPDATE_STATUS_LABELS : CREATE_STATUS_LABELS}
      />
    );

    return (
      <KModal
        showModal={isOpen}
        closeModal={this.handleClose}
        header={header}
        footer={footer}
        width={400}
        className="onboarding-folder-modal"
      >
        <div className="onboarding-folder-modal__content">
          <div className="onboarding-folder-modal__header">
            <KuikaModalIconWrapper iconName="folder" iconType="sidebar-icons" intent={intent} />
            <KuikaH5>{header}</KuikaH5>
          </div>
          <div className="onboarding-folder-modal__field">
            <KuikaSmallText>Folder Name *</KuikaSmallText>
            <KuikaInput value={name} onChange={this.handleNameChange} placeholder="Enter folder name" autoFocus />
          </div>
          <div className="onboarding-folder-modal__field">
            <KuikaSmallText>Parent Folder</KuikaSmallText>
            <KuikaSelectbox
              value={parentId || "root"}
              onChange={this.handleParentChange}
              options={this.getAvailableParentFolders()}
            />
          </div>
        </div>
      </KModal>
    );
  }
}
